import { formatCurrency } from "../../lib/formatters.js";
import {
  EXPENSE_TYPE_IRREGULAR,
  EXPENSE_TYPE_ONGOING,
  getExpenseTypeTranslationKey,
  normalizeExpenseType
} from "../../lib/expenseType.js";

function parseText(value) {
  return String(value ?? "").trim();
}

function parseAmount(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseDay(value) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return 1;
  }

  return Math.max(1, Math.min(31, Math.trunc(parsed)));
}

function getMonthKeys() {
  return [
    "january",
    "february",
    "march",
    "april",
    "mayFull",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december"
  ];
}

function normalizeDateParts(dateText, activeYear) {
  const normalizedYear = String(activeYear ?? "").trim();
  const month = String(dateText ?? "").slice(5, 7);
  const day = String(dateText ?? "").slice(8, 10);

  return {
    year: normalizedYear,
    month: /^\d{2}$/.test(month) ? month : "01",
    day: /^\d{2}$/.test(day) ? day : "01"
  };
}

function maxDayForMonth(year, monthValue) {
  const safeYear = Number(String(year ?? "").trim()) || new Date().getFullYear();
  const safeMonth = Number(monthValue) || 1;
  return new Date(safeYear, safeMonth, 0).getDate();
}

function buildScopedDate(year, monthValue, dayValue) {
  const normalizedYear = String(year ?? "").trim();
  const normalizedMonth = String(monthValue ?? "01").padStart(2, "0");
  const clampedDay = Math.max(1, Math.min(
    maxDayForMonth(normalizedYear, normalizedMonth),
    Number(dayValue ?? 1) || 1
  ));

  return `${normalizedYear}-${normalizedMonth}-${String(clampedDay).padStart(2, "0")}`;
}

const expenseTypeValues = [
  EXPENSE_TYPE_ONGOING,
  EXPENSE_TYPE_IRREGULAR
];

export function createExpenseGridColumns({
  activeYear = "",
  categoryOptions = [],
  paymentMethodOptions = [],
  supplierOptions = [],
  serviceOptions = [],
  t = (key) => key
} = {}) {
  const expenseTypeLabelByValue = new Map(
    expenseTypeValues.map((value) => [value, t(getExpenseTypeTranslationKey(value))])
  );
  const expenseTypeOptions = expenseTypeValues.map((value) => ({
    value,
    label: expenseTypeLabelByValue.get(value) ?? value
  }));
  const monthOptions = getMonthKeys().map((key, index) => ({
    value: String(index + 1).padStart(2, "0"),
    label: t(key)
  }));
  const monthLabelByValue = new Map(monthOptions.map((option) => [option.value, option.label]));
  const getCategoryOptions = (row) => {
    const selectedCategoryId = String(row?.categoryId ?? "");
    const selectedCategoryLabel = String(row?.category ?? "").trim();
    const includesSelectedCategory = categoryOptions.some((option) => option.value === selectedCategoryId);

    if (!selectedCategoryId || !selectedCategoryLabel || includesSelectedCategory) {
      return categoryOptions;
    }

    return [
      {
        value: selectedCategoryId,
        label: selectedCategoryLabel
      },
      ...categoryOptions
    ];
  };
  const applyCategoryDraft = (row, draftValue) => {
    const resolvedOption = getCategoryOptions(row).find((option) => option.value === draftValue);

    if (!resolvedOption) {
      return {
        ...row,
        categoryId: row.categoryId,
        category: row.category
      };
    }

    return {
      ...row,
      categoryId: resolvedOption.value,
      category: resolvedOption.label
    };
  };

  return [
  {
    key: "dateMonth",
    label: t("month"),
    type: "text",
    editable: true,
    align: "text",
    inputType: "text",
    editor: {
      kind: "combobox",
      options: monthOptions,
      allowCustomValue: false,
      maxVisibleOptions: 6
    },
    sortValue: (row) => String(row.date ?? ""),
    getDraftValue: (row) => normalizeDateParts(row.date, activeYear).month,
    parseDraft: parseText,
    applyDraft: (row, draftValue) => {
      const current = normalizeDateParts(row.date, activeYear);

      return {
        ...row,
        date: buildScopedDate(activeYear, draftValue || current.month, Number(current.day))
      };
    },
    displayValue: (row) => {
      const { month } = normalizeDateParts(row.date, activeYear);
      return monthLabelByValue.get(month) ?? "";
    }
  },
  {
    key: "dateDay",
    label: t("day"),
    type: "number",
    editable: true,
    align: "number",
    inputType: "number",
    min: "1",
    max: "31",
    sortValue: (row) => String(row.date ?? ""),
    getDraftValue: (row) => String(Number(normalizeDateParts(row.date, activeYear).day)),
    parseDraft: parseDay,
    applyDraft: (row, draftValue) => {
      const current = normalizeDateParts(row.date, activeYear);

      return {
        ...row,
        date: buildScopedDate(activeYear, current.month, draftValue)
      };
    },
    displayValue: (row) => String(Number(normalizeDateParts(row.date, activeYear).day))
  },
  {
    key: "amount",
    label: t("amount"),
    type: "currency",
    editable: true,
    align: "number",
    inputType: "number",
    step: "0.01",
    getDraftValue: (row) => String(row.amount ?? 0),
    parseDraft: parseAmount,
    displayValue: (row) => formatCurrency(row.amount)
  },
  {
    key: "method",
    label: t("method"),
    type: "text",
    editable: true,
    align: "text",
    inputType: "text",
    editor: {
      kind: "combobox",
      options: paymentMethodOptions,
      allowCustomValue: true,
      maxVisibleOptions: 5
    },
    getDraftValue: (row) => String(row.method ?? ""),
    parseDraft: parseText,
    displayValue: (row) => row.method
  },
  {
    key: "type",
    label: t("type"),
    type: "text",
    editable: true,
    align: "text",
    inputType: "text",
    editor: {
      kind: "combobox",
      options: expenseTypeOptions,
      allowCustomValue: false,
      maxVisibleOptions: 4
    },
    getDraftValue: (row) => String(row.type ?? ""),
    parseDraft: parseText,
    displayValue: (row) => expenseTypeLabelByValue.get(normalizeExpenseType(row.type)) ?? row.type
  },
  {
    key: "categoryId",
    label: t("category"),
    type: "text",
    editable: true,
    align: "text",
    inputType: "text",
    editor: {
      kind: "combobox",
      options: getCategoryOptions,
      allowCustomValue: false,
      maxVisibleOptions: 5
    },
    getDraftValue: (row) => String(row.categoryId ?? ""),
    parseDraft: parseText,
    applyDraft: applyCategoryDraft,
    displayValue: (row) => row.category
  },
  {
    key: "supplier",
    label: t("supplier"),
    type: "text",
    editable: true,
    align: "text",
    inputType: "text",
    editor: {
      kind: "autocomplete",
      options: supplierOptions
    },
    getDraftValue: (row) => String(row.supplier ?? ""),
    parseDraft: parseText,
    displayValue: (row) => row.supplier
  },
  {
    key: "service",
    label: t("service"),
    type: "text",
    editable: true,
    align: "text",
    inputType: "text",
    editor: {
      kind: "autocomplete",
      options: serviceOptions
    },
    getDraftValue: (row) => String(row.service ?? ""),
    parseDraft: parseText,
    displayValue: (row) => row.service
  }
  ];
}
